import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { auditTrailService } from '../../../lib/auditTrailService';
import { useAuth } from '../../../contexts/AuthContext';

const SecurityEventsPanel = ({ onRefresh }) => {
  const { userProfile } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadSecurityEvents();
  }, []);

  const loadSecurityEvents = async () => {
    setLoading(true);
    try {
      const { data } = await auditTrailService?.getSecurityEvents({ limit: 50 });
      setEvents(data || []);
    } catch (error) {
      console.error('Error loading security events:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleResolve = async (eventId) => {
    const { error } = await auditTrailService?.resolveSecurityEvent(
      eventId,
      userProfile?.id,
      'Resolved from audit dashboard'
    );
    if (!error) {
      loadSecurityEvents();
      onRefresh?.();
    }
  };

  const getSeverityColor = (severity) => {
    const colors = {
      critical: 'var(--color-error)',
      error: 'var(--color-error)',
      warning: 'var(--color-warning)',
      info: 'var(--color-info)'
    };
    return colors?.[severity] || 'var(--color-muted)';
  };

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl font-heading font-semibold text-foreground">Security Events</h3>
        <Button variant="outline" size="sm" onClick={loadSecurityEvents}>
          <Icon name="RefreshCw" size={16} className="mr-2" />
          Refresh
        </Button>
      </div>
      <div className="space-y-3">
        {loading ? (
          <div className="text-center py-12">
            <Icon name="Loader" size={32} className="animate-spin text-primary mx-auto" />
          </div>
        ) : events?.length === 0 ? (
          <div className="text-center py-12">
            <Icon name="Shield" size={48} className="mx-auto mb-4 text-success" />
            <p className="text-muted-foreground">No security events detected</p>
          </div>
        ) : (
          events?.map((event) => (
            <div key={event?.id} className="p-4 border border-border rounded-lg">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h4 className="text-sm font-medium text-foreground">
                      {event?.event_type?.replace(/_/g, ' ')?.toUpperCase()}
                    </h4>
                    <span
                      className="px-2 py-0.5 rounded text-xs font-medium"
                      style={{
                        backgroundColor: `${getSeverityColor(event?.severity)}15`,
                        color: getSeverityColor(event?.severity)
                      }}
                    >
                      {event?.severity}
                    </span>
                    {event?.resolved && (
                      <span className="px-2 py-0.5 rounded text-xs font-medium bg-success/15 text-success">
                        Resolved
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground mb-2">{event?.description}</p>
                  <div className="flex items-center gap-4 text-xs text-muted-foreground">
                    <span>{event?.user?.full_name || 'Unknown'}</span>
                    <span>{event?.ip_address || 'N/A'}</span>
                    <span>{new Date(event?.created_at)?.toLocaleString()}</span>
                  </div>
                </div>
                {!event?.resolved && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleResolve(event?.id)}
                  >
                    Resolve
                  </Button>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default SecurityEventsPanel;